import { ChevronDownIcon, SearchIcon } from "lucide-react-native";
import React, { memo } from "react";
import { ListRenderItem } from "react-native";

import {
  IDialCodePickerViewProps,
  TDialCodeItem,
} from "@/features/auth/types/dialCodePickerTypes";

import { Input } from "@/components/atoms/Input";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectFlatList,
  SelectIcon,
  SelectInput,
  SelectPortal,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { TEST_ID_SIGNIN_DIAL_CODE_SEARCH } from "e2e/testId";

const defaultKeyExtractor = (item: unknown) => (item as TDialCodeItem).code;

/**
 * View component for dial code picker that renders country code selection UI
 * @param root0 - Component props
 * @param root0.options - Array of filtered country dial code options to display in the list
 * @param root0.selectedValue - Currently selected dial code value
 * @param root0.defaultValue - Default dial code value to show when nothing is selected
 * @param root0.placeholder - Placeholder text shown in the select trigger
 * @param root0.searchQuery - Current search query string for filtering countries
 * @param root0.renderItem - Function to render each country code item in the list
 * @param root0.onChange - Callback fired when user selects a different dial code
 * @param root0.getItemLayout - Function to calculate item layout for FlatList optimization
 * @param root0.onEndReached - Callback fired when user scrolls to the end of the list
 * @param root0.onSearchChange - Callback fired when search input text changes
 * @param root0.onSearchInputReset - Callback fired when search input should be cleared
 * @param root0.listAreaStyle - Concrete height for the list area, computed by the container (80% of window) because percentage heights are circular inside the content-sized actionsheet
 */
const DialCodePickerView = ({
  options,
  selectedValue,
  defaultValue,
  placeholder,
  searchQuery,
  renderItem,
  onChange,
  getItemLayout,
  onEndReached,
  onSearchChange,
  onSearchInputReset,
  listAreaStyle,
}: IDialCodePickerViewProps) => (
  <Select
    defaultValue={defaultValue}
    onValueChange={onChange}
    onClose={onSearchInputReset}
    className="w-24 rounded-none border-0 bg-surface-subtle dark:bg-surface-raised"
  >
    <SelectTrigger
      variant="outline"
      size="xl"
      className="border-0 p-0 pr-3"
      testID="SIGNIN.DIAL_CODE_TRIGGER"
      // The dial code lives in a SelectInput, whose VALUE iOS does not publish
      // to the accessibility tree — captured hierarchy showed this trigger with
      // an empty label and no "+39" node anywhere on the screen. So the control
      // had no accessible name for VoiceOver, and the native e2e flow could not
      // observe the country it had just selected. Name it explicitly.
      accessibilityLabel={`Country dial code ${selectedValue || defaultValue || placeholder}`}
    >
      <SelectInput
        placeholder={placeholder}
        value={selectedValue || ""}
        className="flex-1 px-0 text-center font-body text-content-primary"
      />
      <SelectIcon as={ChevronDownIcon} />
    </SelectTrigger>
    <SelectPortal>
      <SelectBackdrop />
      <SelectContent>
        <SelectDragIndicatorWrapper className="pb-4 pt-2">
          <SelectDragIndicator />
        </SelectDragIndicatorWrapper>
        <Stack
          className="w-full gap-4 pt-4"
          // eslint-disable-next-line no-restricted-syntax -- runtime-computed 80%-of-window height; percentage heights are circular inside the content-sized actionsheet on Android (Cesena login outage 2026-08-03)
          UNSAFE_style={listAreaStyle}
        >
          <Input
            className="h-12 rounded-sm"
            leadingIcon={SearchIcon}
            field={{
              value: searchQuery,
              placeholder: "Search...",
              onChangeText: onSearchChange,
              // SE-6812: gives the field a real identity for web. It does
              // NOT surface in the iOS accessibility tree (verified on a
              // rebuilt binary), so the native flow anchors on a country row
              // rather than on this id — see the constant's docblock.
              testID: TEST_ID_SIGNIN_DIAL_CODE_SEARCH,
            }}
          />

          <SelectFlatList
            data={options}
            renderItem={renderItem as ListRenderItem<unknown>}
            keyExtractor={defaultKeyExtractor}
            className="w-full"
            getItemLayout={getItemLayout}
            removeClippedSubviews
            initialNumToRender={20}
            onEndReached={onEndReached}
            windowSize={30}
          />
        </Stack>
      </SelectContent>
    </SelectPortal>
  </Select>
);

DialCodePickerView.displayName = "DialCodePickerView";

export default memo(DialCodePickerView);
